import { useEffect, useState } from "react";
import MDBox from "components/MDBox";
import { useMaterialUIController } from "context";
import CTLTable from "application/components/CTLTable";
import { getColumns, Attributes, operationURLS } from "./model";
import CTLAdvancedFilter from "application/components/CTLAdvancedFilter";
import CTLNotification from "application/components/CTLNotification";
import { IconButton, TextField, Tooltip, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TodayIcon from "@mui/icons-material/Today";

function formatDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function buildDateFilters(dateStr) {
  return [
    {
      direction: "from",
      dataType: "date",
      fieldName: "dateTime",
      value: `${dateStr}T00:00`,
    },
    {
      direction: "to",
      dataType: "date",
      fieldName: "dateTime",
      value: `${dateStr}T23:59`,
    },
  ];
}

function extractDateFromFilters(filters) {
  const fromFilter = filters.find(
    (f) => f.fieldName === "dateTime" && f.direction === "from"
  );
  if (fromFilter && fromFilter.value) {
    return fromFilter.value.substring(0, 10);
  }
  return null;
}

function FsVoicePicker({
  onLoad,
  pageId,
  searchText,
  registerCallBacks,
  onCall,
  pageName,
}) {
  const [controller] = useMaterialUIController();
  const { activePage, tableValue, darkMode } = controller;
  const [openFilter, setOpenFilter] = useState(false);
  const [updateList, setUpdateList] = useState(Date.now());
  const [columns, setColumns] = useState(getColumns());
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "",
    status: "",
  });

  const today = formatDate(new Date());
  const [selectedDate, setSelectedDate] = useState(today);
  const [advancedFilters, setAdvancedFilters] = useState(
    buildDateFilters(today)
  );

  const changeDate = (dateStr) => {
    setSelectedDate(dateStr);
    // Keep non-date filters, replace date filters
    const nonDateFilters = advancedFilters.filter(
      (f) => f.fieldName !== "dateTime"
    );
    setAdvancedFilters([...buildDateFilters(dateStr), ...nonDateFilters]);
    setUpdateList(Date.now());
  };

  const goToPrevDay = () => {
    const d = new Date(selectedDate + "T12:00:00");
    d.setDate(d.getDate() - 1);
    changeDate(formatDate(d));
  };

  const goToNextDay = () => {
    const d = new Date(selectedDate + "T12:00:00");
    d.setDate(d.getDate() + 1);
    const nextDate = formatDate(d);
    if (nextDate <= today) {
      changeDate(nextDate);
    }
  };

  const goToToday = () => {
    changeDate(today);
  };

  const isToday = selectedDate === today;

  function onOpenFilter() {
    setOpenFilter(true);
  }

  function onCloseFilter() {
    setOpenFilter(false);
  }

  function onApplyFilter(filters) {
    if (!filters || filters.length === 0) {
      // Filter cleared — reset to today
      setSelectedDate(today);
      setAdvancedFilters(buildDateFilters(today));
      setUpdateList(Date.now());
    } else {
      setAdvancedFilters(filters);
      // Sync date navigator with whatever date the user set in advanced filter
      const dateFromFilter = extractDateFromFilters(filters);
      if (dateFromFilter) {
        setSelectedDate(dateFromFilter);
      }
    }
    setOpenFilter(false);
  }

  useEffect(() => {
    Attributes && onLoad(Attributes);
    registerCallBacks &&
      registerCallBacks({
        openAdvancedFilter: onOpenFilter,
      });
  }, [activePage, tableValue]);

  const iconColor = (disabled) =>
    disabled
      ? darkMode
        ? "#ffffff40"
        : "#d2d6da"
      : darkMode
      ? "#ffffffcc"
      : "#344767";

  const dateNavigator = (
    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
      <Tooltip title="Previous Day">
        <IconButton onClick={goToPrevDay} size="small" sx={{ padding: "2px" }}>
          <ChevronLeftIcon sx={{ color: iconColor(false), fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      <TextField
        type="date"
        size="small"
        value={selectedDate}
        onChange={(e) => e.target.value && changeDate(e.target.value)}
        inputProps={{ max: today }}
        sx={{
          width: "140px",
          "& .MuiInputBase-input": {
            color: darkMode ? "#ffffffcc" : "#344767",
            fontSize: "0.7rem",
            padding: "2px 6px",
          },
          "& .MuiOutlinedInput-root": {
            "& fieldset": { borderColor: darkMode ? "#ffffff40" : "#d2d6da" },
            "&:hover fieldset": {
              borderColor: darkMode ? "#ffffff80" : "#344767",
            },
          },
        }}
      />

      <Tooltip title="Next Day">
        <span>
          <IconButton
            onClick={goToNextDay}
            size="small"
            disabled={isToday}
            sx={{ padding: "2px" }}
          >
            <ChevronRightIcon
              sx={{ color: iconColor(isToday), fontSize: 18 }}
            />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Go to Today">
        <span>
          <IconButton
            onClick={goToToday}
            size="small"
            disabled={isToday}
            sx={{ padding: "2px" }}
          >
            <TodayIcon sx={{ color: iconColor(isToday), fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>

      <Typography
        variant="caption"
        sx={{
          color: darkMode ? "#ffffff80" : "#7b809a",
          fontSize: "0.7rem",
          ml: 0.5,
        }}
      >
        {new Date(selectedDate + "T12:00:00").toLocaleDateString(undefined, {
          weekday: "short",
          year: "numeric",
          month: "short",
          day: "numeric",
        })}
      </Typography>
    </div>
  );

  return (
    <MDBox>
      <CTLTable
        apiURL={operationURLS.list}
        columns={columns}
        advancedFilters={advancedFilters}
        onClearFilters={() => {
          changeDate(today);
        }}
        entriesPerPage={false}
        canSearch
        defaultSortDirection="desc"
        defaultSortColumn="dateTime"
        pageId={pageId}
        searchText={searchText}
        hasRowSelection={false}
        updateList={updateList}
        onCall={onCall}
        footerContent={dateNavigator}
      />
      <CTLAdvancedFilter
        entityName="Recording Explorer"
        columns={getColumns()}
        applyFilter={openFilter}
        onApplyFilter={onApplyFilter}
        onCloseFilter={onCloseFilter}
        advancedFilters={advancedFilters}
      />
      <CTLNotification notify={notify} setNotify={setNotify} />
    </MDBox>
  );
}

export default FsVoicePicker;
